import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import BrandLogo from '@/components/feature/BrandLogo';
import { useFavorites } from '@/hooks/useFavorites';
import { mediaList } from '@/mocks/media';
import { MEDIA_TYPE_LABEL } from '@/types/media';

const NAV_ITEMS = [
  { to: '/', label: '首页', end: true },
  { to: '/category/movie', label: '电影' },
  { to: '/category/tv', label: '电视剧' },
  { to: '/category/variety', label: '综艺' },
  { to: '/about', label: '关于' },
];

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [favOpen, setFavOpen] = useState(false);
  const [query, setQuery] = useState('');
  const favRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { favorites, count } = useFavorites();

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (favRef.current && !favRef.current.contains(event.target as Node)) {
        setFavOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = query.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search');
    setMobileOpen(false);
  };

  const favoritedItems = mediaList.filter((item) => favorites.includes(item.id));

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
      isActive ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'
    }`;

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-6">
            <BrandLogo />
            <nav className="hidden md:flex items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className={navClass}>
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <form onSubmit={submitSearch} className="hidden lg:flex items-center">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="search"
                  name="q"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索影视名称、类型或主演"
                  className="w-56 xl:w-72 h-9 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
            </form>

            <div className="relative" ref={favRef}>
              <button
                type="button"
                onClick={() => setFavOpen((v) => !v)}
                className="relative w-9 h-9 rounded-full flex items-center justify-center text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="我的收藏"
              >
                <i className="ri-heart-3-line text-lg"></i>
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-background-50 text-[10px] font-bold flex items-center justify-center">
                    {count}
                  </span>
                )}
              </button>
              {favOpen && (
                <div className="absolute right-0 mt-2 w-72 rounded-lg border border-background-200 bg-background-50 shadow-lg p-2 z-50">
                  <p className="px-2 py-1.5 text-xs font-semibold text-foreground-500">我的收藏（{count}）</p>
                  {favoritedItems.length === 0 ? (
                    <p className="px-2 py-4 text-sm text-foreground-400 text-center">暂无收藏，快去收藏喜欢的影视吧</p>
                  ) : (
                    <ul className="max-h-72 overflow-y-auto">
                      {favoritedItems.map((item) => (
                        <li key={item.id}>
                          <Link
                            to={`/detail/${item.id}`}
                            onClick={() => setFavOpen(false)}
                            className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-background-100 transition-colors"
                          >
                            <img src={item.poster} alt={item.title} className="w-9 h-12 rounded object-cover object-top" />
                            <span className="flex flex-col min-w-0">
                              <span className="text-sm font-medium text-foreground-900 truncate">{item.title}</span>
                              <span className="text-xs text-foreground-500">
                                {MEDIA_TYPE_LABEL[item.type]} · {item.year}
                              </span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden w-9 h-9 rounded-full flex items-center justify-center text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="切换导航菜单"
              aria-expanded={mobileOpen}
            >
              <i className={mobileOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 space-y-4">
          <form onSubmit={submitSearch}>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="search"
                name="q"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索影视名称、类型或主演"
                className="w-full h-10 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
          </form>
          <nav className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                    isActive ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}